import React, { useEffect, useRef } from "react";

export default function SignaturePad({
  value,
  onChange,
  disabled = false,
  error = "",
  height = 160,
}) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });

  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;

    // חשוב: width חייב להיות ממשי
    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;

    if (!cssWidth || !cssHeight) return;

    const w = Math.floor(cssWidth * dpr);
    const h = Math.floor(cssHeight * dpr);

    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;

      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0f172a";
    }
  };

  const getPoint = (clientX, clientY) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const drawLine = (from, to) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  // ✅ ציור חתימה קיימת (URL או dataUrl)
  useEffect(() => {
    setupCanvas();
    const canvas = canvasRef.current;
    if (!canvas) return;

    clearCanvas();
    if (!value) return;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.clientWidth, canvas.clientHeight);
    };
    img.src = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // ✅ חיבור Native events עם passive:false (פותח את הבאג של React+iOS)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // חשוב: לבטל scroll/zoom על האזור
    canvas.style.touchAction = "none";

    const onStart = (clientX, clientY) => {
      if (disabled) return;
      setupCanvas();
      drawingRef.current = true;
      lastPointRef.current = getPoint(clientX, clientY);
    };

    const onMove = (clientX, clientY) => {
      if (disabled || !drawingRef.current) return;
      const p = getPoint(clientX, clientY);
      drawLine(lastPointRef.current, p);
      lastPointRef.current = p;
    };

    const onEnd = () => {
      if (!drawingRef.current) return;
      drawingRef.current = false;

      const dataUrl = canvas.toDataURL("image/png");
      onChange?.(dataUrl);
    };

    // Pointer (מודרני)
    const handlePointerDown = (e) => {
      e.preventDefault();
      canvas.setPointerCapture?.(e.pointerId);
      onStart(e.clientX, e.clientY);
    };
    const handlePointerMove = (e) => {
      e.preventDefault();
      onMove(e.clientX, e.clientY);
    };
    const handlePointerUp = (e) => {
      e.preventDefault();
      canvas.releasePointerCapture?.(e.pointerId);
      onEnd();
    };

    // Touch (Fallback) - Passive false
    const handleTouchStart = (e) => {
      e.preventDefault();
      const t = e.touches[0];
      if (!t) return;
      onStart(t.clientX, t.clientY);
    };
    const handleTouchMove = (e) => {
      e.preventDefault();
      const t = e.touches[0];
      if (!t) return;
      onMove(t.clientX, t.clientY);
    };
    const handleTouchEnd = (e) => {
      e.preventDefault();
      onEnd();
    };

    // מאזינים
    canvas.addEventListener("pointerdown", handlePointerDown);
    canvas.addEventListener("pointermove", handlePointerMove);
    canvas.addEventListener("pointerup", handlePointerUp);
    canvas.addEventListener("pointercancel", handlePointerUp);

    canvas.addEventListener("touchstart", handleTouchStart, { passive: false });
    canvas.addEventListener("touchmove", handleTouchMove, { passive: false });
    canvas.addEventListener("touchend", handleTouchEnd, { passive: false });
    canvas.addEventListener("touchcancel", handleTouchEnd, { passive: false });

    // גם resize יכול לשבור DPI באייפון (היפוך/toolbar)
    const onResize = () => setupCanvas();
    window.addEventListener("resize", onResize);

    // init
    setTimeout(() => setupCanvas(), 0);

    return () => {
      canvas.removeEventListener("pointerdown", handlePointerDown);
      canvas.removeEventListener("pointermove", handlePointerMove);
      canvas.removeEventListener("pointerup", handlePointerUp);
      canvas.removeEventListener("pointercancel", handlePointerUp);

      canvas.removeEventListener("touchstart", handleTouchStart);
      canvas.removeEventListener("touchmove", handleTouchMove);
      canvas.removeEventListener("touchend", handleTouchEnd);
      canvas.removeEventListener("touchcancel", handleTouchEnd);

      window.removeEventListener("resize", onResize);
    };
  }, [disabled, onChange]);

  const clear = () => {
    clearCanvas();
    onChange?.("");
  };

  return (
    <div dir="rtl" className="w-full md:col-span-2 flex flex-col items-center justify-center">
      <p className="text-sm font-semibold text-slate-800">חתימה:</p>

      {/* ✅ חשוב: לתת רוחב אמיתי (w-full) ולא רק max-w */}
      <div
        className={`mt-3 w-full max-w-lg rounded-xl border overflow-hidden bg-white
          ${error ? "border-red-300" : "border-slate-300"}
          ${disabled ? "opacity-60" : ""}
        `}
      >
        <canvas
          ref={canvasRef}
          style={{ height, width: "100%" }}
          className="block"
        />
      </div>

      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      <p className="text-xs text-slate-500 mt-2">חתום/י בתוך המסגרת (בעכבר או באצבע).</p>

      <button
        type="button"
        disabled={disabled}
        onClick={clear}
        className="text-xs text-red-600 hover:underline disabled:opacity-50"
      >
        נקה חתימה
      </button>
    </div>
  );
}
